// Cache system for Admin Client Library

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  expiresAt: number;
  key: string;
}

export interface CacheConfig {
  defaultTTL?: number; // Time to live in milliseconds
  maxSize?: number; // Maximum number of entries
  enableLogging?: boolean;
}

export class AdminClientCache {
  private cache: Map<string, CacheEntry> = new Map();
  private config: Required<CacheConfig>;
  private cleanupInterval?: NodeJS.Timeout;

  constructor(config: CacheConfig = {}) {
    this.config = {
      defaultTTL: config.defaultTTL || 5 * 60 * 1000, // 5 minutes default
      maxSize: config.maxSize || 100,
      enableLogging: config.enableLogging || false,
    };

    // Start automatic cleanup every 2 minutes
    this.startCleanupInterval();
  }

  // Start automatic cleanup interval
  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(
      () => {
        this.cleanExpired();
      },
      2 * 60 * 1000
    ); // Every 2 minutes
  }

  // Stop automatic cleanup interval
  private stopCleanupInterval(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
  }

  // Destroy cache and cleanup
  public destroy(): void {
    this.stopCleanupInterval();
    this.cache.clear();
    this.log('Cache DESTROYED');
  }

  // Generate cache key from API endpoint and parameters
  generateKey(endpoint: string, params?: any): string {
    const paramsString = params ? JSON.stringify(this.sortObject(params)) : '';
    return `${endpoint}:${paramsString}`;
  }

  // Sort object keys for consistent cache keys
  private sortObject(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.sortObject(item));
    }
    const sorted: any = {};
    Object.keys(obj)
      .sort()
      .forEach((key) => {
        sorted[key] = this.sortObject(obj[key]);
      });
    return sorted;
  }

  // Get data from cache
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      this.log(`Cache MISS: ${key}`);
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      this.log(`Cache EXPIRED: ${key}`);
      return null;
    }

    this.log(`Cache HIT: ${key}`);
    return entry.data as T;
  }

  // Set data in cache
  set<T>(key: string, data: T, ttl?: number): void {
    const now = Date.now();
    const timeToLive = ttl || this.config.defaultTTL;

    // Check if we need to evict old entries
    if (this.cache.size >= this.config.maxSize) {
      this.evictOldest();
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + timeToLive,
      key,
    };

    this.cache.set(key, entry);
    this.log(`Cache SET: ${key} (TTL: ${timeToLive}ms)`);
  }

  // Remove specific entry
  delete(key: string): boolean {
    const result = this.cache.delete(key);
    if (result) {
      this.log(`Cache DELETE: ${key}`);
    }
    return result;
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
    this.log('Cache CLEARED');
  }

  // Invalidate cache entries by pattern
  invalidatePattern(pattern: string): number {
    let count = 0;
    const regex = new RegExp(pattern);

    for (const key of Array.from(this.cache.keys())) {
      if (regex.test(key)) {
        this.cache.delete(key);
        count++;
      }
    }

    this.log(`Cache INVALIDATED ${count} entries matching pattern: ${pattern}`);
    return count;
  }

  // Invalidate cache entries by endpoint
  invalidateEndpoint(endpoint: string): number {
    return this.invalidatePattern(`^${endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:`);
  }

  // Evict oldest entry
  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTimestamp = Infinity;

    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.log(`Cache EVICTED oldest entry: ${oldestKey}`);
    }
  }

  // Clean expired entries
  cleanExpired(): number {
    const now = Date.now();
    let count = 0;

    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        count++;
      }
    }

    if (count > 0) {
      this.log(`Cache CLEANED ${count} expired entries`);
    }
    return count;
  }

  // Get cache statistics
  getStats(): {
    size: number;
    maxSize: number;
    defaultTTL: number;
    entries: Array<{ key: string; timestamp: number; expiresAt: number; timeToExpire: number }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      timestamp: entry.timestamp,
      expiresAt: entry.expiresAt,
      timeToExpire: Math.max(0, entry.expiresAt - now),
    }));

    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      defaultTTL: this.config.defaultTTL,
      entries,
    };
  }

  // Check if key exists and is not expired
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  // Update cache configuration
  updateConfig(newConfig: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.log(`Cache config updated: ${JSON.stringify(newConfig)}`);
  }

  // Logging helper
  private log(message: string): void {
    if (this.config.enableLogging) {
      console.log(`[AdminClientCache] ${message}`);
    }
  }

  // Preload/warm up cache with specific data
  warmUp<T>(key: string, data: T, ttl?: number): void {
    this.set(key, data, ttl);
    this.log(`Cache WARMED UP: ${key}`);
  }
}

// Default cache instance
export const defaultCache = new AdminClientCache({
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  maxSize: 200,
  enableLogging: process.env.NODE_ENV === 'development',
});

// Cache TTL constants for different types of data
export const CacheTTL = {
  SHORT: 1 * 60 * 1000, // 1 minute
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 15 * 60 * 1000, // 15 minutes
  VERY_LONG: 60 * 60 * 1000, // 1 hour
} as const;
